import { useEffect, useCallback } from "react";
import { useRecoilState } from "recoil";
import styled from "styled-components";

import { ClipFullModalState } from "../../../State/modals";
import { FloatingContainer, Modal } from "../../Wrappers";
import { EClipPageSource, ModalType } from "../../../@types";
import { CloseIcon } from "./styledComponents";
import Close from "../../../../assets/images/floatingannouncement/close.svg";
import ClipExperience from "MVVM/Views/ClipExperience";
import history from "../../../../history";
import { AdUnit } from "MVVM/Components/Ads";
import { useMediaQuery } from "MVVM/Hooks/useMediaQuery";

const ClipModal = () => {
  const [clipFullModalState, setClipFullModalState] =
    useRecoilState(ClipFullModalState);
  const { isOpen, shareId, orientation } = clipFullModalState;
  const showSideAds = useMediaQuery("(min-width: 1400px)");
  const showBillboard = useMediaQuery("(min-width: 1200px)");
  const closeModal = useCallback(() => {
    setClipFullModalState({
      ...clipFullModalState,
      isOpen: ModalType.None,
      contextOverride: null,
    });
  }, [clipFullModalState, setClipFullModalState]);

  useEffect(() => {
    if (isOpen !== ModalType.Clip) return;

    const unlisten = history.listen(() => {
      closeModal();
    });

    return () => {
      unlisten();
    };
  }, [isOpen, closeModal]);

  if (isOpen !== ModalType.Clip) return <></>;
  return (
    <Modal
      isModalOpen={isOpen === ModalType.Clip}
      setModalOpen={() => null}
      handleModalClose={closeModal}
      disableOutsideClose={undefined}
      style={{ overflow: "auto" }}
    >
      <StyledFloatingContainer>
        <CloseIcon
          size={40}
          src={Close}
          onClick={() => closeModal()}
          style={{ zIndex: 2 }}
        />

        {shareId && (
          <ClipExperience
            clipId={shareId}
            source={EClipPageSource.MODAL}
            orientation={orientation}
            clipContext={clipFullModalState.contextOverride}
          />
        )}
      </StyledFloatingContainer>
      {showSideAds && (
        <>
          <SideAdOverlay $position="left">
            <AdUnit
              slotId="clip-modal-left-skyscraper"
              adUnitType="large-skyscraper"
              collapseIfEmpty
              rounded
              showUpgradeCta
            />
          </SideAdOverlay>
          <SideAdOverlay $position="right">
            <AdUnit
              slotId="clip-modal-right-skyscraper"
              adUnitType="large-skyscraper"
              collapseIfEmpty
              rounded
              showUpgradeCta
            />
          </SideAdOverlay>
        </>
      )}
      {showBillboard && (
        <BillboardOverlay>
          <AdUnit
            slotId="clip-modal-billboard"
            adUnitType="billboard"
            collapseIfEmpty
            rounded
          />
        </BillboardOverlay>
      )}
    </Modal>
  );
};

const StyledFloatingContainer = styled(FloatingContainer)`
  align-items: stretch;
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.ned};
  box-shadow: 0 0 0px #233438;
  flex-direction: column;
  height: 65%;
  justify-content: center;
  max-width: 1680px;
  padding: 8px;
  position: absolute;
  top: 20px;
  width: 60%;
  z-index: 101;

  @media (max-width: ${(props) => props.theme.breaks.standard}) {
    width: 90%;
  }
`;

const SideAdOverlay = styled.div<{ $position: "left" | "right" }>`
  position: fixed;
  top: 36%;
  ${({ $position }) => ($position === "left" ? "left: 24px;" : "right: 24px;")}
  transform: translateY(-50%);
  width: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  pointer-events: auto;

  @media (min-width: 1600px) {
    ${({ $position }) =>
      $position === "left"
        ? "left: calc(25% - 15vw - 150px);"
        : "right: calc(25% - 15vw - 150px);"}
  }
`;

const BillboardOverlay = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 5px;
  display: flex;
  justify-content: center;
  z-index: 100;
  pointer-events: auto;
`;

export { ClipModal };
